import { useEffect, useRef } from 'react';
import { useProvisionGroupsMutation } from 'network/mutations/useProvisionGroupsMutation';
import { InstallationStatusMessage } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { Loader } from '../../Loader';

const GROUP_STATUS_CREATED = 'created';

/**
 * Multi-group onboarding result step (B-v1a). Calls provision-groups once on
 * mount — the BE creates one installation per pasted group and dispatches the
 * per-group webhook install asynchronously — then shows the per-group outcome.
 */
export const ProvisionGroupsResults = () => {
  const { setIsPending, setIsNextEnabled } = useMultiStepDialog();
  const {
    mutate: provisionGroups,
    data,
    isPending,
    isError,
    reset,
  } = useProvisionGroupsMutation();
  const hasTriggered = useRef(false);

  const spawnStep = spawn('dialogs.gitlab-installation.provision-groups');

  useEffect(() => {
    if (hasTriggered.current) return;

    hasTriggered.current = true;
    provisionGroups();
  }, []);

  useEffect(() => {
    setIsPending(isPending);
  }, [isPending]);

  useEffect(() => {
    // Only let the user proceed once at least one group connected — mirrors the
    // single-group flow, which enables Next only after a successful connection.
    setIsNextEnabled(!!data && data.succeededCount > 0);
  }, [data]);

  const onRetry = () => {
    reset();
    provisionGroups();
  };

  if (isError) {
    return (
      <InstallationStatusMessage
        status="error"
        message={spawnStep('error')}
        retryLabel={spawnStep('retry-button')}
        onRetry={onRetry}
        isRetrying={isPending}
      />
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="size-6" />
      </div>
    );
  }

  const allSucceeded = data.failedCount === 0;

  return (
    <div className="flex flex-col gap-4">
      <InstallationStatusMessage
        status={allSucceeded ? 'success' : 'error'}
        message={
          allSucceeded
            ? spawnStep('success', { count: data.succeededCount })
            : spawnStep('partial-failure', {
                succeeded: data.succeededCount,
                failed: data.failedCount,
              })
        }
      />
      <ul className="flex flex-col gap-1.5">
        {data.results.map((result) => {
          const created = result.status === GROUP_STATUS_CREATED;
          const statusClassName = created
            ? 'text-text-tag-positive-active'
            : 'text-text-tag-negative-active';

          return (
            <li
              key={result.groupPath}
              className="flex items-center justify-between gap-4 text-sm"
            >
              <span className="text-text-general-neutral-strong">
                {result.groupPath}
              </span>
              <span className={cn('shrink-0', statusClassName)}>
                {created
                  ? spawnStep('group-status.created')
                  : result.error ?? spawnStep('group-status.failed')}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
